/**
 * Helpers de exibição da inbox de mensageria interna (Item #8b, Slice C).
 *
 * Resolvem o que o CONTRATO não traz, degradando honesto (lição do projeto:
 * nomes resolvidos à parte como no Painel da Equipe; degradar honesto se faltar).
 *
 * CONTRATO (R15) — `ThreadSummary` traz só `kind`, `participantUserIds[]`,
 * `anchorConversationId` (um id), `lastMessageAt`, `lastMessagePreview`, `unread`.
 * NÃO há nome de time nem rótulo rico de âncora. Por isso:
 *   - thread `group` → rótulo genérico honesto ("Canal do time"), sem inventar
 *     nome de time (honest-defer: nome do time não está no contrato do thread).
 *   - âncora → chip genérico "Sobre uma conversa" deep-link pelo id (honest-defer:
 *     rótulo rico da âncora não está no contrato).
 */

import type { ThreadSummary } from './types';
import type { PresenceWireItem } from '../../lib/api-client';

/** Rótulo honesto do canal de grupo (nome do time não vem no contrato do thread). */
export const GROUP_THREAD_LABEL = 'Canal do time';

/**
 * Nome da contraparte de um thread 1:1: o participante cujo id ≠ o usuário atual,
 * resolvido pela presença. Degrada honesto: nome ausente → null (a UI cai para
 * iniciais do id / "Operador").
 */
export function directCounterpartName(
  thread: ThreadSummary,
  currentUserId: string | null,
  presenceByUserId: Map<string, string>,
): string | null {
  const otherId = thread.participantUserIds.find((id) => id !== currentUserId);
  if (otherId === undefined) return null;
  const name = presenceByUserId.get(otherId);
  return name !== undefined && name.length > 0 ? name : null;
}

/** Id da contraparte de um thread 1:1 (≠ usuário atual), ou null. */
export function directCounterpartId(
  thread: ThreadSummary,
  currentUserId: string | null,
): string | null {
  return thread.participantUserIds.find((id) => id !== currentUserId) ?? null;
}

/** Mapa `userId → name` (só os com nome resolvido) a partir da presença. */
export function presenceNameMap(presence: PresenceWireItem[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const p of presence) {
    if (p.name !== null && p.name.length > 0) map.set(p.userId, p.name);
  }
  return map;
}

/**
 * Mapa `userId → name` a partir dos `participantNames` resolvidos pelo backend
 * (caminho B — diretório de nomes). Fonte completa (via `User.name`), substitui a
 * presença na resolução de NOME (título 1:1 + remetente dos balões em grupo).
 */
export function participantNameMap(
  participantNames: { userId: string; name: string }[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const p of participantNames) {
    if (p.name.length > 0) map.set(p.userId, p.name);
  }
  return map;
}

/**
 * Título do thread na LISTA: grupo → rótulo de canal; 1:1 → `counterpartName`
 * resolvido pelo backend, com fallback honesto ("Operador {id curto}") se ausente.
 */
export function listThreadTitle(thread: ThreadSummary, currentUserId: string | null): string {
  if (thread.kind === 'group') return GROUP_THREAD_LABEL;
  if (thread.counterpartName !== null && thread.counterpartName.length > 0) {
    return thread.counterpartName;
  }
  const id = directCounterpartId(thread, currentUserId);
  return id !== null ? `Operador ${id.slice(0, 6)}` : 'Operador';
}

/** Contagem de não-lido formatada para a pill, com cap "9+". */
export function unreadDisplay(unread: number): string {
  if (unread <= 0) return '';
  return unread > 9 ? '9+' : unread.toString();
}

/**
 * Rótulo de participantes do canal de grupo no cabeçalho da ThreadView.
 * Genérico honesto — o nome do time NÃO vem no contrato do thread (honest-defer).
 * Ex.: "Canal do time · 5 participantes".
 */
export function groupParticipantsLabel(participantCount: number): string {
  const suffix = participantCount === 1 ? 'participante' : 'participantes';
  return `${GROUP_THREAD_LABEL} · ${participantCount.toString()} ${suffix}`;
}

/**
 * Nome de exibição de um remetente (bubble do outro), resolvido pela presença.
 * Degrada honesto: nome ausente → "Operador {id curto}", ou "Operador" sem id.
 */
export function senderDisplayName(
  senderUserId: string,
  presenceByUserId: Map<string, string>,
): string {
  const name = presenceByUserId.get(senderUserId);
  if (name !== undefined && name.length > 0) return name;
  return senderUserId.length > 0 ? `Operador ${senderUserId.slice(0, 6)}` : 'Operador';
}

/**
 * Nome de exibição da contraparte de um thread 1:1, a partir dos participantes do
 * detalhe (o detalhe não tem helper próprio de contraparte como o summary). Degrada
 * honesto igual ao `senderDisplayName`.
 */
export function detailCounterpartName(
  participantUserIds: string[],
  currentUserId: string | null,
  presenceByUserId: Map<string, string>,
): string {
  const otherId = participantUserIds.find((id) => id !== currentUserId);
  if (otherId === undefined) return 'Operador';
  return senderDisplayName(otherId, presenceByUserId);
}
